import React, { useState, useEffect, useMemo, useCallback, useRef } from "react";
import Button from "components/Button";
import Modal from "components/Modal";
import Select from "components/Select";
import { IOption } from "models/Form";
import { VISIBILITY_LIST } from "constants/haulpay-broker";
import Icon from "@mdi/react";
import {
  mdiFileDocumentOutline,
  mdiFilePdfBox,
  mdiFileExcel,
  mdiFileWordBox,
  mdiFileImage,
  mdiChevronLeft,
  mdiChevronRight,
  mdiMagnifyPlus,
  mdiMagnifyMinus
} from "@mdi/js";
import MultiSelect from "components/MultiSelect";
import { useUserFeature } from "hooks/useUserFeature";
import { Document, Page, pdfjs } from "react-pdf";
import "react-pdf/dist/Page/AnnotationLayer.css";
import "react-pdf/dist/Page/TextLayer.css";
import { showErrorMessage } from "../../../../../../../helpers/toastMessage";
// Configure PDF.js worker for production builds
// Use CDN for reliability, or fallback to local build
pdfjs.GlobalWorkerOptions.workerSrc =
  process.env.NODE_ENV === "production"
    ? "/static/media/pdf.worker.min.mjs"
    : `//unpkg.com/pdfjs-dist@${pdfjs.version}/build/pdf.worker.min.mjs`;

interface DocumentPreviewModalProps {
  isOpen: boolean;
  onClose: () => void;
  file: {
    id: string;
    name: string;
    preview?: string;
    category?: IOption[];
    type?: string;
    visibleTo?: string[];
  };
  categories: IOption[];
  onCategoryChange: (id: string, category: IOption[]) => void;
  onVisibilityChange?: (id: string, visibleTo: string[]) => void;
  onUpload?: () => Promise<void>;
  isUploading?: boolean;
  uploadProgress?: number;
  isCompressing?: boolean;
  compressionProgress?: number;
  isAssignedLoadsPage?: boolean;
}

const shortInputClass =
  "placeholder:text-slate-400 !bg-input text-slate-700 !text-base desktop:!text-sm border border-slate-200 !rounded !px-4 !py-3 desktop:!py-2 !h-12 desktop:!h-10 transition duration-300 ease focus:outline-none focus:border-slate-400 hover:border-slate-300 shadow-sm focus:shadow";

const selectBtnClass =
  "!py-3 desktop:!py-2 !h-12 desktop:!h-10 !text-base desktop:!text-sm w-full !bg-background";

const normalizeList = (list: string[]) =>
  [...list]
    .map((item) => item.trim())
    .filter(Boolean)
    .sort();

const findVisibilityOption = (list?: string[]) => {
  if (!list || !list.length) return undefined;
  const normalizedList = normalizeList(list);
  return VISIBILITY_LIST.find((option) => {
    const optionValues = normalizeList(String(option.value).split(","));
    return (
      optionValues.length === normalizedList.length &&
      optionValues.every((value, idx) => value === normalizedList[idx])
    );
  });
};

const getForcedVisibilityValue = (isAssignedLoadsPage?: boolean) => {
  const index = isAssignedLoadsPage ? 2 : 0;
  const raw = VISIBILITY_LIST[index]?.value ?? "";
  return raw
    .split(",")
    .map((item) => item.trim())
    .filter(Boolean);
};

export const DocumentPreviewModal: React.FC<DocumentPreviewModalProps> = ({
  isOpen,
  onClose,
  file,
  categories,
  onCategoryChange,
  onVisibilityChange,
  onUpload,
  isUploading = false,
  uploadProgress = 0,
  isCompressing = false,
  compressionProgress = 0,
  isAssignedLoadsPage = false
}) => {
  const { isCarrier } = useUserFeature();
  const [visibleTo, setVisibleTo] = useState<IOption | undefined>();
  const [numPages, setNumPages] = useState<number | null>(null);
  const [pageNumber, setPageNumber] = useState<number>(1);
  const [zoom, setZoom] = useState<number>(1); // 1 = 100%, 1.5 = 150%, etc.
  const [sheetExpanded, setSheetExpanded] = useState<boolean>(false);
  const [pageWidth, setPageWidth] = useState<number>(400);
  const documentWrapperRef = useRef<HTMLDivElement>(null);
  const sheetTouchStartY = useRef<number | null>(null);
  const sheetStartScrollTop = useRef<number>(0);
  const sheetSwipeHandled = useRef<boolean>(false);

  const canEditVisibility = !isCarrier && !isAssignedLoadsPage;
  const forcedVisibilityValue = useMemo(
    () => getForcedVisibilityValue(isAssignedLoadsPage),
    [isAssignedLoadsPage]
  );

  useEffect(() => {
    if (!isOpen) setSheetExpanded(false);
  }, [isOpen]);

  const handleSheetTouchStart = (e: React.TouchEvent<HTMLElement>) => {
    sheetTouchStartY.current = e.touches[0].clientY;
    sheetStartScrollTop.current = e.currentTarget.scrollTop;
  };

  const handleSheetTouchEnd = (e: React.TouchEvent<HTMLElement>) => {
    const startY = sheetTouchStartY.current;
    sheetTouchStartY.current = null;
    if (startY === null) return;
    const deltaY = e.changedTouches[0].clientY - startY;
    if (!sheetExpanded && deltaY < -30) {
      sheetSwipeHandled.current = true;
      setSheetExpanded(true);
    } else if (sheetExpanded && deltaY > 30 && sheetStartScrollTop.current <= 0) {
      sheetSwipeHandled.current = true;
      setSheetExpanded(false);
    }
  };

  const handleSheetHandleClick = () => {
    if (sheetSwipeHandled.current) {
      sheetSwipeHandled.current = false;
      return;
    }
    setSheetExpanded((prev) => !prev);
  };

  const updatePageWidth = useCallback(() => {
    if (documentWrapperRef.current) {
      const containerWidth = documentWrapperRef.current.clientWidth;
      const padding = 60;
      setPageWidth(Math.min(containerWidth - padding, 400));
    } else {
      const padding = 60;
      setPageWidth(Math.min(window.innerWidth - padding, 400));
    }
  }, []);

  useEffect(() => {
    if (!isOpen) return;

    if (!canEditVisibility) {
      if (!isCarrier) {
        setVisibleTo(undefined);
      }
      if (onVisibilityChange) {
        onVisibilityChange(file.id, forcedVisibilityValue);
      }
    } else {
      setVisibleTo(findVisibilityOption(file.visibleTo));
    }

    // Reset PDF page number and zoom
    setPageNumber(1);
    setNumPages(null);
    setZoom(1);
    updatePageWidth();
  }, [
    isOpen,
    file.visibleTo,
    isCarrier,
    forcedVisibilityValue,
    file.id,
    file.preview,
    onVisibilityChange,
    canEditVisibility,
    updatePageWidth
  ]);

  const handleVisibilityChange = useCallback(
    (value: IOption) => {
      setVisibleTo(value);
      if (onVisibilityChange) {
        onVisibilityChange(file.id, String(value.value).split(","));
      }
    },
    [onVisibilityChange, file.id]
  );

  // Function to determine which icon to show based on file type
  const getFileIcon = () => {
    const fileType = file.type?.toLowerCase() || "";

    if (fileType.includes("pdf")) {
      return mdiFilePdfBox;
    } else if (
      fileType.includes("excel") ||
      fileType.includes("spreadsheet") ||
      fileType.includes("xlsx") ||
      fileType.includes("xls")
    ) {
      return mdiFileExcel;
    } else if (fileType.includes("word") || fileType.includes("doc") || fileType.includes("docx")) {
      return mdiFileWordBox;
    } else if (fileType.includes("image")) {
      return mdiFileImage;
    } else {
      return mdiFileDocumentOutline;
    }
  };

  // Check if file is a PDF (memoized to prevent re-renders)
  const isPDF = useMemo(
    () => file.type?.toLowerCase().includes("pdf") || file.name?.toLowerCase().endsWith(".pdf"),
    [file.type, file.name]
  );

  const onDocumentLoadSuccess = useCallback(
    ({ numPages }: { numPages: number }) => {
      setNumPages(numPages);
      updatePageWidth();
    },
    [updatePageWidth]
  );

  const onDocumentLoadError = useCallback((error: Error) => {
    showErrorMessage("Failed to load PDF:" + error);
  }, []);

  // Memoize the file object to prevent unnecessary re-renders
  const documentFile = useMemo(() => {
    if (!file.preview) return undefined;
    return { url: file.preview };
  }, [file.preview]);

  // Memoize whether we should show PDF controls
  const showPDFControls = useMemo(() => Boolean(isPDF && file.preview), [isPDF, file.preview]);

  const showPageNavigation = useMemo(
    () => Boolean(numPages && numPages > 1 && showPDFControls),
    [numPages, showPDFControls]
  );

  // Memoize zoom handlers
  const handleZoomIn = useCallback(() => {
    setZoom((z) => Math.min(3, z + 0.25));
  }, []);

  const handleZoomOut = useCallback(() => {
    setZoom((z) => Math.max(0.5, z - 0.25));
  }, []);

  // Memoize page navigation handlers
  const handlePrevPage = useCallback(() => {
    setPageNumber((p) => Math.max(1, p - 1));
  }, []);

  const handleNextPage = useCallback(() => {
    setPageNumber((p) => Math.min(numPages!, p + 1));
  }, [numPages]);

  useEffect(() => {
    if (!isOpen) return;

    updatePageWidth();

    const handleResize = () => {
      updatePageWidth();
    };

    window.addEventListener("resize", handleResize);
    window.addEventListener("orientationchange", handleResize);

    let resizeObserver: ResizeObserver | undefined;
    if (documentWrapperRef.current && typeof ResizeObserver !== "undefined") {
      resizeObserver = new ResizeObserver(handleResize);
      resizeObserver.observe(documentWrapperRef.current);
    }

    return () => {
      window.removeEventListener("resize", handleResize);
      window.removeEventListener("orientationchange", handleResize);
      resizeObserver?.disconnect();
    };
  }, [isOpen, updatePageWidth]);

  return (
    <Modal
      isOpen={isOpen}
      closeModal={onClose}
      size="lg"
      wrapperClass="!my-auto !py-2 sm:!py-4 mx-auto mobile:!h-full mobile:!p-0 mobile:!m-0 mobile:!w-full mobile:!max-w-full"
      dialogClass="!bg-foreground !p-0 !pt-2 sm:!pt-4 !border-none !min-h-72 !h-full desktop:!h-auto !max-h-full desktop:!max-h-none desktop:!overflow-hidden mobile:!w-full mobile:!max-w-full mobile:!rounded-none"
      closeClass="!top-2 !right-2 desktop:!top-10 desktop:!right-7 !bg-card/90 hover:!bg-card !backdrop-blur-md !text-text !border !border-gray-600/40 !shadow-lg !z-50 !size-12 desktop:!size-11 desktop:!bg-transparent desktop:!border-0 desktop:!shadow-none [&_svg]:!w-7 [&_svg]:!h-7 desktop:[&_svg]:!w-5 desktop:[&_svg]:!h-5"
      showCloseButton>
      {file && (
        <div className="m-1 sm:m-5 h-full flex flex-col desktop:block desktop:h-auto">
          <div className="px-3 pt-2 pb-3 pr-14 desktop:pr-3 flex flex-shrink-0">
            <div className="text-base font-medium desktop:text-sm desktop:font-normal text-text desktop:text-gray-400 truncate">
              {file.name || "Document"}
            </div>
          </div>
          <div className="grid grid-cols-1 gap-4 items-start relative flex-1 min-h-0 desktop:flex-initial">
            {/* Document Preview */}
            <div className="col-span-1 min-w-0">
              <div
                ref={documentWrapperRef}
                className="m-2 p-3 bg-card rounded-2xl border border-gray-700/30 desktop:m-0 desktop:p-0 desktop:bg-transparent desktop:rounded-none desktop:border-0">
                {isPDF && file.preview ? (
                  <div className="p-2 sm:p-4 flex flex-col items-center justify-center rounded-lg overflow-auto h-[60dvh] desktop:h-auto desktop:max-h-[70vh] touch-pan-y">
                    <Document
                      file={documentFile}
                      onLoadSuccess={onDocumentLoadSuccess}
                      onLoadError={onDocumentLoadError}
                      loading={
                        <div className="flex items-center justify-center p-4 sm:p-8">
                          <span className="text-xs sm:text-sm">Loading PDF...</span>
                        </div>
                      }
                      error={
                        <div className="flex items-center justify-center p-4 sm:p-8">
                          <span className="text-xs sm:text-sm text-red-400">
                            Failed to load PDF.
                          </span>
                        </div>
                      }>
                      <Page
                        pageNumber={pageNumber}
                        width={pageWidth * zoom}
                        renderTextLayer={false}
                        renderAnnotationLayer={false}
                      />
                    </Document>
                  </div>
                ) : (
                  <div className="p-6 flex flex-col items-center justify-center h-[60dvh] desktop:h-auto">
                    <div className="mb-4 p-6 bg-gray-800/40 rounded-lg">
                      <Icon path={getFileIcon()} size={6} className="text-gray-300" />
                    </div>
                    <p className="text-sm text-gray-400 text-center mt-2 max-w-md truncate">
                      {file.name}
                    </p>
                  </div>
                )}
              </div>
            </div>

            {/* Options Panel — tap-the-handle-to-expand bottom sheet on mobile, side panel on desktop */}
            <div
              onTouchStart={handleSheetTouchStart}
              onTouchEnd={handleSheetTouchEnd}
              className={`!bg-card desktop:!bg-foreground col-span-1 absolute left-0 right-0 bottom-0 z-30 transition-[top] duration-[600ms] ease-[cubic-bezier(0.32,0.72,0,1)] rounded-t-2xl shadow-2xl border-t border-x border-gray-600/40 desktop:static desktop:z-auto desktop:overflow-visible desktop:rounded-lg desktop:border desktop:border-gray-700/20 desktop:shadow-none desktop:transition-none desktop:left-auto desktop:right-auto desktop:bottom-auto desktop:!top-auto p-4 pt-1 desktop:p-3 flex flex-col gap-4 desktop:gap-3 desktop:mt-2 desktop:h-full ${
                sheetExpanded ? "top-[12dvh] overflow-y-auto" : "top-[68dvh] overflow-hidden"
              }`}>
              {/* Mobile-only handle — tap or swipe to expand/collapse */}
              <button
                type="button"
                onClick={handleSheetHandleClick}
                className="desktop:hidden sticky top-0 z-20 -mx-4 px-4 py-3 flex justify-center bg-card rounded-t-2xl touch-manipulation cursor-pointer"
                aria-label={sheetExpanded ? "Collapse controls panel" : "Expand controls panel"}>
                <div className="w-12 h-1.5 rounded-full bg-gray-400/70" />
              </button>
              {/* PDF Controls */}
              {showPDFControls && (
                <div className="flex flex-col gap-3 desktop:gap-3">
                  {/* Zoom Controls */}
                  <div className="flex items-center justify-between gap-3">
                    <span className="text-sm desktop:text-xs font-medium">Zoom</span>
                    <div className="flex items-center gap-3">
                      <button
                        type="button"
                        onClick={handleZoomOut}
                        disabled={zoom <= 0.5}
                        className="p-3 desktop:p-2 bg-gray-700 hover:bg-gray-600 active:bg-gray-500 rounded-md transition-colors disabled:opacity-50 disabled:cursor-not-allowed touch-manipulation"
                        aria-label="Zoom out"
                        title="Zoom out">
                        <Icon
                          path={mdiMagnifyMinus}
                          size={0.9}
                          className="text-gray-200 desktop:scale-75"
                        />
                      </button>
                      <span className="text-base desktop:text-xs min-w-[3rem] text-center">
                        {Math.round(zoom * 100)}%
                      </span>
                      <button
                        type="button"
                        onClick={handleZoomIn}
                        disabled={zoom >= 3}
                        className="p-3 desktop:p-2 bg-gray-700 hover:bg-gray-600 active:bg-gray-500 rounded-md transition-colors disabled:opacity-50 disabled:cursor-not-allowed touch-manipulation"
                        aria-label="Zoom in"
                        title="Zoom in">
                        <Icon
                          path={mdiMagnifyPlus}
                          size={0.9}
                          className="text-gray-200 desktop:scale-75"
                        />
                      </button>
                    </div>
                  </div>

                  {/* Page Navigation */}
                  {showPageNavigation && (
                    <div className="flex items-center justify-between gap-3">
                      <span className="text-sm desktop:text-xs font-medium">Page</span>
                      <div className="flex items-center gap-3">
                        <button
                          type="button"
                          onClick={handlePrevPage}
                          disabled={pageNumber <= 1}
                          className="p-3 desktop:p-2 bg-gray-700 hover:bg-gray-600 active:bg-gray-500 rounded-md transition-colors disabled:opacity-50 disabled:cursor-not-allowed touch-manipulation"
                          aria-label="Previous page">
                          <Icon
                            path={mdiChevronLeft}
                            size={0.9}
                            className="text-gray-200 desktop:scale-75"
                          />
                        </button>
                        <span className="text-base desktop:text-xs min-w-[4rem] sm:min-w-[5rem] text-center">
                          {pageNumber} of {numPages}
                        </span>
                        <button
                          type="button"
                          onClick={handleNextPage}
                          disabled={numPages !== null && pageNumber >= numPages}
                          className="p-3 desktop:p-2 bg-gray-700 hover:bg-gray-600 active:bg-gray-500 rounded-md transition-colors disabled:opacity-50 disabled:cursor-not-allowed touch-manipulation"
                          aria-label="Next page">
                          <Icon
                            path={mdiChevronRight}
                            size={0.9}
                            className="text-gray-200 desktop:scale-75"
                          />
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              )}
              {/* Category & Visible To */}
              <div
                className={`grid grid-cols-1 ${
                  !isCarrier ? "sm:grid-cols-2" : "sm:grid-cols-1"
                } gap-3`}>
                <div>
                  <label className="block text-sm desktop:text-xs font-medium mb-1.5">
                    Category
                  </label>
                  <MultiSelect
                    options={categories}
                    value={file.category || []}
                    onChange={(value) => onCategoryChange(file.id, value as IOption[])}
                    placeholder="Select category"
                    className="w-full"
                    btnClass={selectBtnClass}
                    arrowClass="!right-3 !text-gray-400"
                    inputClass={shortInputClass}
                  />
                </div>
                {canEditVisibility && (
                  <div>
                    <label className="block text-sm desktop:text-xs font-medium mb-1.5">
                      Visible To
                    </label>
                    <Select
                      options={VISIBILITY_LIST}
                      value={visibleTo}
                      onChange={handleVisibilityChange}
                      placeholder="Visible To"
                      className="w-full"
                      btnClass={selectBtnClass}
                      arrowClass="!right-3 !text-gray-400"
                      inputClass={shortInputClass}
                    />
                  </div>
                )}
              </div>

              {/* Actions */}
              <div className="mt-auto flex flex-col gap-2 pt-2 border-t border-gray-700/20">
                {onUpload && (
                  <div className="relative">
                    <Button
                      variant="outline"
                      onClick={onUpload}
                      className="w-full px-4 !py-3 desktop:!py-2 !h-12 desktop:!h-11 text-base desktop:text-sm relative overflow-hidden"
                      disabled={isUploading || isCompressing}>
                      <span className="relative z-10">
                        {isCompressing
                          ? `Compressing... ${Math.round(compressionProgress)}%`
                          : isUploading
                            ? `Uploading... ${Math.round(uploadProgress)}%`
                            : "Upload Now"}
                      </span>
                      {(isCompressing || isUploading) && (
                        <div
                          className="absolute left-0 top-0 h-full bg-primary-400 opacity-30 transition-all duration-300"
                          style={{
                            width: `${isCompressing ? compressionProgress : uploadProgress}%`
                          }}
                        />
                      )}
                    </Button>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </Modal>
  );
};
